import { last } from "common-utils/utils/array";
import { DisplayableError } from "kreate-common/modules/displayable-error";
import {
  getInfiniteResourceKey$ExplorePolicyList,
  httpGet$ExplorePolicyList,
} from "kreate-server/commands/ExplorePolicyList/fetcher";
import {
  ExplorePolicyList$Params,
  ExplorePolicyList$Result,
} from "kreate-server/commands/ExplorePolicyList/typing";
import {
  getInfiniteResourceKey$GetGalleryOfferList,
  httpGet$GetGalleryOfferList,
} from "kreate-server/commands/GetGalleryOfferList/fetcher";
import {
  GetGalleryOfferList$Params,
  GetGalleryOfferList$Result,
} from "kreate-server/commands/GetGalleryOfferList/typing";
import {
  getInfiniteResourceKey$GetSecondaryArtworkFilterData,
  httpGet$GetSecondaryArtworkFilterData,
} from "kreate-server/commands/GetSecondaryArtworkFilterData/fetcher";
import {
  GetSecondaryArtworkFilterData$Params,
  GetSecondaryArtworkFilterData$Result,
} from "kreate-server/commands/GetSecondaryArtworkFilterData/typing";
import {
  getInfiniteResourceKey$GetSecondaryArtworkList,
  httpPost$GetSecondaryArtworkList,
} from "kreate-server/commands/GetSecondaryArtworkList/fetcher";
import {
  GetSecondaryArtworkList$Params,
  GetSecondaryArtworkList$Result,
} from "kreate-server/commands/GetSecondaryArtworkList/typing";
import {
  getInfiniteResourceKey$GetSecondaryOffers,
  httpGet$GetSecondaryOffers,
} from "kreate-server/commands/GetSecondaryOffers/fetcher";
import {
  GetSecondaryOffers$Params,
  GetSecondaryOffers$Result,
} from "kreate-server/commands/GetSecondaryOffers/typing";
import { InfiniteResourceKey } from "kreate-server/types";
import useSWRInfinite from "swr/infinite";

import { StatusIndicator } from "../types";

type OffsetLimit = {
  offset?: number | null | undefined;
  limit?: number | null | undefined;
};

type PageData<R> = {
  result: R;
  cursor: OffsetLimit | undefined;
};

export function generateHook<P extends OffsetLimit, R>({
  getKey,
  getUrl,
  fetcher,
  getLength,
}: {
  getKey: (params: P) => InfiniteResourceKey<P>;
  getUrl: (params: P) => string;
  fetcher: (url: string, params: P) => Promise<R>;
  getLength: (result: R) => number;
}) {
  return function useCommandResultInfinite(
    initialParams: (P & Required<OffsetLimit>) | null | undefined
  ) {
    const swr = useSWRInfinite<PageData<R>>(
      (
        pageIndex: number,
        previousPageData: PageData<R> | undefined
      ): InfiniteResourceKey<P> | undefined => {
        if (!initialParams) return undefined;
        if (!pageIndex) return getKey(initialParams);
        const { offset, limit } = previousPageData?.cursor || {};
        if (offset == null || limit == null) return undefined;
        return getKey({ ...initialParams, offset, limit });
      },
      async ({ params }: { params: P }): Promise<PageData<R>> => {
        const result = await fetcher(getUrl(params), params).catch((e) => {
          throw DisplayableError.from(e, `Failed to fetch: ${getUrl(params)}`);
        });
        const cursor =
          params.offset != null &&
          params.limit != null &&
          getLength(result) === params.limit
            ? { offset: params.offset + params.limit, limit: params.limit }
            : undefined;
        return { result, cursor };
      }
    );

    const isEnded = !swr.isLoading && swr.size > 0 && !last(swr.data)?.cursor;

    const loadMore =
      !swr.isLoading && !isEnded ? () => swr.setSize((s) => s + 1) : undefined;

    const isEmpty =
      !swr.isLoading &&
      swr.size > 0 &&
      swr.data?.every((d) => !getLength(d.result));

    const isLoadingMore =
      swr.isLoading || (!!swr.data && swr.size > swr.data.length);

    const statusIndicator: StatusIndicator = isLoadingMore
      ? "loading"
      : isEmpty
      ? "empty"
      : isEnded
      ? "ended"
      : "has-more";

    return {
      data: swr.data?.flatMap((d) => d.result),
      error: swr.error,
      loadMore,
      swr,
      statusIndicator,
      initialParams,
    };
  };
}

export const useCommandResultInfinite$GetSecondaryArtworkList = generateHook<
  GetSecondaryArtworkList$Params,
  GetSecondaryArtworkList$Result
>({
  getKey: getInfiniteResourceKey$GetSecondaryArtworkList,
  getUrl: () => `/api/v1/secondary-page/artwork-list`,
  fetcher: httpPost$GetSecondaryArtworkList,
  getLength: (result) => result.artworks.length,
});

export const useCommandResultInfinite$GetSecondaryArtworkFilterData =
  generateHook<
    GetSecondaryArtworkFilterData$Params,
    GetSecondaryArtworkFilterData$Result
  >({
    getKey: getInfiniteResourceKey$GetSecondaryArtworkFilterData,
    getUrl: () => `/api/v1/secondary-page/filter-data`,
    fetcher: httpGet$GetSecondaryArtworkFilterData,
    getLength: (result) => result.docs.length,
  });

export const useCommandResultInfinite$ExplorePolicyList = generateHook<
  ExplorePolicyList$Params,
  ExplorePolicyList$Result
>({
  getKey: getInfiniteResourceKey$ExplorePolicyList,
  getUrl: () => `/api/v1/secondary-page/policy-list`,
  fetcher: httpGet$ExplorePolicyList,
  getLength: (result) => result.policies.length,
});

export const useCommandResultInfinite$GetSecondaryOffers = generateHook<
  GetSecondaryOffers$Params,
  GetSecondaryOffers$Result
>({
  getKey: getInfiniteResourceKey$GetSecondaryOffers,
  getUrl: () => `/api/v1/secondary/offers`,
  fetcher: httpGet$GetSecondaryOffers,
  getLength: (result) => result.offers.length,
});

export const useCommandResultInfinite$GetGalleryOfferList = generateHook<
  GetGalleryOfferList$Params,
  GetGalleryOfferList$Result
>({
  getKey: getInfiniteResourceKey$GetGalleryOfferList,
  getUrl: () => `/api/v1/collector-gallery/offer-list`,
  fetcher: httpGet$GetGalleryOfferList,
  getLength: (result) => result.offers.length,
});
